/* eslint-disable react-hooks/exhaustive-deps, @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useState } from "react"
import type { Dispatch, RefObject, SetStateAction } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import isString from "lodash-es/isString"
import { useUnmount, useUpdateEffect } from "react-use"

import { reduxBatch, safeTransition } from "@/3514/utils"
import type { TEmptyAsyncCallback, TEmptyCallback } from "@/shared/types/functions"

import { useCoachingChatSocketReceiver as useSocketReceiver } from "../api"
import { coachingChatUiConfig as uiConfig } from "../config"
import { useCoachingChatContext as useChatContext } from "../context"
import {
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EApiMode,
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationErrorVariant as EConversationErrorVariant,
    ECoachingChatConversationMessagePhase as EConversationMessagePhase,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatSocketListenerEventName as ESocketEventName,
    type TCoachingChatMessageAuthor as TMessageAuthor,
    type TCoachingChatSocketErrorEventResponse as TSocketErrorEventResponse,
    type TCoachingChatSocketMessageEventResponse as TSocketMessageEventResponse,
    type TCoachingChatSummaryCard as TSummaryCard
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

import {
    type TCoachingChatRestartConversationFunction as TConversationRestarterFunction,
    useCoachingChatConversationRestarter as useConversationRestarter
} from "./coaching-chat.conversation-restart.hooks"
import { useCoachingChatSocketErrorStreamerListener as useErrorListener } from "./coaching-chat.socket-errors.hooks"

const { STREAMING_CHUNKS_THRESHOLD_GAP } = uiConfig

const {
    streamingUtils: { getSummariesFromChunkString, extractMessageFromBuffer }
} = uiUtils

type TDispatchPayload = {
    participantMessageId?: number
    assistantMessageId?: number
    conversationId: number
    role: TMessageAuthor
    finalMessage: string
    summaries: TSummaryCard[]
}

type TState = {
    isDone: boolean
    isProgressing: boolean
    value: string
    id: number | null
    isDelayed: boolean
}

type TCoachingChatMessageStreamer = {
    onStreamingFailed: TEmptyAsyncCallback
    onAnimationComplete: TEmptyCallback
} & Omit<TState, "id">

function useCoachingChatMessageStreamer(): TCoachingChatMessageStreamer {
    const {
        entityId,
        conversation: { availableSummaryCards }
    } = useChatContext()

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const restartConversation: TConversationRestarterFunction = useConversationRestarter({ entityId })

    const isListening: boolean =
        eq(conversationApiMode, EApiMode.Sockets) &&
        includes<EConversationStatus>([EConversationStatus.Created, EConversationStatus.InProgress], conversationStatus)

    const messageBuffer: RefObject<string> = useRef<string>("")

    const dispatchPayloadRef: RefObject<TDispatchPayload> = useRef<TDispatchPayload>(null)

    const [state, setState]: [TState, Dispatch<SetStateAction<TState>>] = useState<TState>({
        isDone: false,
        isProgressing: false,
        value: String(""),
        id: null,
        isDelayed: false
    })

    const delayTimeoutRef: RefObject<NodeJS.Timeout | null> = useRef<NodeJS.Timeout | null>(null)

    const lastChunkTimeRef: RefObject<number | null> = useRef<number | null>(null)

    const chunkGapTimeoutRef: RefObject<NodeJS.Timeout | null> = useRef<NodeJS.Timeout | null>(null)

    const resetDelayTimer: TEmptyCallback = useCallback((): void => {
        if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current)

        setState((prev: TState): TState => (prev.isDelayed ? { ...prev, isDelayed: false } : prev))

        delayTimeoutRef.current = setTimeout((): void => {
            setState((prev: TState): TState => (prev.isProgressing ? { ...prev, isDelayed: true } : prev))
        }, 10000)
    }, [])

    useUnmount((): void => {
        if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current)
        if (chunkGapTimeoutRef.current) clearTimeout(chunkGapTimeoutRef.current)

        setState({
            isDone: false,
            isProgressing: false,
            value: String(""),
            id: null,
            isDelayed: false
        })
        messageBuffer.current = String("")
        dispatchPayloadRef.current = null
    })

    useErrorListener({
        entityId,
        handleError: async (err: TSocketErrorEventResponse): Promise<void> => {
            if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current)
            if (chunkGapTimeoutRef.current) clearTimeout(chunkGapTimeoutRef.current)

            setState((prev: TState): TState => ({ ...prev, isProgressing: false, isDelayed: false }))

            if (err.retryable && err.phase === "streaming" && err.suggested_action === "cm_restart") {
                if (isNumber(err.participant_message_id)) await restartConversation(err.participant_message_id)
                else await restartConversation()
            }
        }
    })

    useSocketReceiver<TSocketMessageEventResponse>({
        isEnabled: isListening,
        event: ESocketEventName.Message,
        handler: (resp: TSocketMessageEventResponse): void =>
            void safeTransition((): void => {
                const updatedBuffer: string = messageBuffer.current + resp.content
                messageBuffer.current = updatedBuffer

                const now: number = Date.now()
                const timeSinceLastChunk: number = lastChunkTimeRef.current ? now - lastChunkTimeRef.current : 0
                lastChunkTimeRef.current = now

                if (chunkGapTimeoutRef.current) clearTimeout(chunkGapTimeoutRef.current)

                setState((prev: TState): TState => (prev.isDelayed ? { ...prev, isDelayed: false } : prev))

                const partialMessage: string = extractMessageFromBuffer(updatedBuffer)

                if (timeSinceLastChunk > STREAMING_CHUNKS_THRESHOLD_GAP)
                    setState((prev: TState): TState => (prev.isDelayed ? prev : { ...prev, isDelayed: true }))

                resetDelayTimer()

                setState((prev: TState): TState => {
                    if (isNumber(prev.id) && !eq(prev.id, resp.participant_message_id))
                        messageBuffer.current = String("")

                    if (prev.isProgressing) return prev

                    return {
                        ...prev,
                        id: resp.participant_message_id,
                        isProgressing: true
                    }
                })

                if (!resp.done) {
                    setState(
                        (prev: TState): TState =>
                            prev.value === partialMessage ? prev : { ...prev, value: partialMessage }
                    )
                    return
                }

                let finalMessage: string = partialMessage
                try {
                    const parsed: any = JSON.parse(updatedBuffer)

                    if (isString(parsed?.message)) finalMessage = parsed.message

                    dispatchPayloadRef.current = {
                        participantMessageId: resp.participant_message_id,
                        assistantMessageId: resp.assistant_message_id,
                        conversationId: resp.conversation_id,
                        role: resp.role,
                        finalMessage,
                        summaries: getSummariesFromChunkString(updatedBuffer, availableSummaryCards)
                    }

                    if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current)

                    setState(
                        (prev: TState): TState => ({
                            ...prev,
                            id: resp.participant_message_id,
                            value: finalMessage,
                            isProgressing: false,
                            isDone: true,
                            isDelayed: false
                        })
                    )
                } catch (e) {
                    conversationDispatcher.triggerError(EConversationErrorVariant.StreamingFailed)
                }
            })
    })

    const onStreamingFailed: TEmptyAsyncCallback = useCallback(async (): Promise<void> => {
        if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current)

        await restartConversation(state.id)

        conversationDispatcher.removeMessagesRight(1)

        messageBuffer.current = String("")

        dispatchPayloadRef.current = null

        safeTransition((): void =>
            setState({
                isDone: false,
                isProgressing: false,
                value: String(""),
                id: null,
                isDelayed: false
            })
        )
    }, [state.id])

    const onStreamingEnd: TEmptyCallback = useCallback((): void => {
        const payload: TDispatchPayload = dispatchPayloadRef.current

        if (!payload) return

        if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current)

        messageBuffer.current = String("")
        dispatchPayloadRef.current = null

        safeTransition((): void =>
            setState({
                isDone: true,
                isProgressing: false,
                value: payload.finalMessage,
                id: payload.assistantMessageId,
                isDelayed: false
            })
        )

        reduxBatch((): void => {
            conversationDispatcher.updateLastMessage({
                ...(isNumber(payload.assistantMessageId) ? { id: payload.assistantMessageId } : {}),
                content: payload.finalMessage,
                isStreamable: false,
                status: EConversationMessagePhase.Done
            })

            if (!isEmpty(payload.summaries)) {
                conversationDispatcher.populateSummary(payload.summaries)
                conversationDispatcher.changeStatus(EConversationStatus.GeneratingSummary)
            } else conversationDispatcher.markInput({ isDimmed: false, isDisabled: false })
        })
    }, [])

    const onStreamingEndCalledRef: RefObject<boolean> = useRef<boolean>(false)

    useUpdateEffect((): void => {
        if (state.isDone && !onStreamingEndCalledRef.current && document.hidden) {
            onStreamingEndCalledRef.current = true
            onStreamingEnd()
        }
    }, [state.isDone, onStreamingEnd])

    useUpdateEffect((): void => {
        if (!state.isDone) onStreamingEndCalledRef.current = false
    }, [state.isDone])

    const onAnimationComplete: TEmptyCallback = useCallback((): void => {
        if (!document.hidden) void onStreamingEnd()
    }, [])

    const renderedValue: string =
        state.isDelayed && state.isProgressing && state.value ? `${state.value}...` : state.value

    return {
        isDone: state.isDone,
        isProgressing: state.isProgressing,
        value: renderedValue,
        isDelayed: state.isDelayed,
        onStreamingFailed,
        onAnimationComplete
    }
}

export { useCoachingChatMessageStreamer }
export type { TCoachingChatMessageStreamer }
